import React, { useEffect, useRef, useState } from "react";

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2];

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) {
    return "0:00";
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");

  return `${minutes}:${remainingSeconds}`;
};

const QuestionAudioPlayer = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    audio.playbackRate = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setShowSpeedMenu(false);
  }, [audioUrl]);

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const togglePlayback = () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  };

  const seekToPercent = (percent) => {
    const audio = audioRef.current;

    if (!audio || !duration) {
      return;
    }

    const nextTime = Math.max(0, Math.min(100, percent)) * (duration / 100);
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const handleTrackClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const percent = ((event.clientX - rect.left) / rect.width) * 100;

    seekToPercent(percent);
  };

  const handleTrackKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      seekToPercent(progress - 5);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      seekToPercent(progress + 5);
    }
  };

  return (
    <div className="audio__player">
      {audioUrl ? (
        <>
          <audio
            ref={audioRef}
            preload="metadata"
            controlsList="nodownload"
            onLoadedMetadata={(event) =>
              setDuration(event.currentTarget.duration)
            }
            onTimeUpdate={(event) =>
              setCurrentTime(event.currentTarget.currentTime)
            }
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          >
            <source src={audioUrl} type="audio/mpeg" />
          </audio>

          <button
            className="audio__play"
            type="button"
            aria-label={
              isPlaying ? "Pause audio narration" : "Play audio narration"
            }
            onClick={togglePlayback}
          >
            {isPlaying ? (
              <svg aria-hidden="true" viewBox="0 0 24 24">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg aria-hidden="true">
                <use href="#i-play" />
              </svg>
            )}
          </button>

          <span className="audio__time">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div
            className="audio__track"
            role="slider"
            aria-label="Audio progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            tabIndex={0}
            onClick={handleTrackClick}
            onKeyDown={handleTrackKeyDown}
          >
            <i style={{ width: `${progress}%` }} />
          </div>

          <svg className="audio__vol" aria-hidden="true">
            <use href="#i-vol" />
          </svg>

          <div className="audio__more">
            <button
              className="audio__kebab"
              type="button"
              aria-haspopup="true"
              aria-expanded={showSpeedMenu}
              aria-label="Audio settings"
              onClick={() => setShowSpeedMenu(!showSpeedMenu)}
            >
              <svg aria-hidden="true">
                <use href="#i-dots" />
              </svg>
            </button>
            <div
              className="audio__menu"
              role="menu"
              aria-label="Playback speed"
              hidden={!showSpeedMenu}
            >
              <div className="audio__menu-h">Playback speed</div>
              {SPEED_OPTIONS.map((speed) => (
                <button
                  key={speed}
                  type="button"
                  role="menuitemradio"
                  aria-checked={playbackRate === speed}
                  onClick={() => {
                    setPlaybackRate(speed);
                    setShowSpeedMenu(false);
                  }}
                >
                  {speed === 1 ? "Normal (1x)" : `${speed}x`}
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <span className="audio__time">Loading audio...</span>
      )}
    </div>
  );
};

export default QuestionAudioPlayer;
